import { Checkbox, ListItemText, MenuItem, OutlinedInput } from "@mui/material";
import React, { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { StyledSelect } from "./StyledComponents";

const CustomSelectNew = ({
  className = "",
  name,
  id,
  multiple = false,
  value = multiple ? [] : "",
  onChange = () => {},
  options = [],
  dataType,
  defaultValue,          // <- sẽ dùng
  readOnly,
  position,
  disabledUserIds,
  sx,
  ...rest
}) => {
  const { t } = useTranslation();

  const getValue = (option) => {
    switch (dataType) {
      case "leaveType": return option.leaveTypeId;
      case "userGroup": return option.groupCode;
      case "shift": return option.shiftId;
      case "country": return option.alpha3Code;
      case "administratorRights": return option.roleName;      // <- EMPLOYEE
      case "company": return option.companyId;
      case "dropDownShowTime": return option.showTimeID;
      case "userType": return option.value;
      case "user": return option.userId;
      case "group": return option.id;
      case "department": return option.departmentPrefix;
      case "device": return option.deviceId;
      case "selecStatusLeave": return option.statusLeaveId;
      case "permissionId":
        return String(option.userRoleId ?? option.roleId ?? option.id);
      default: return option.value;
    }
  };

  const getLabel = (option) => {
    switch (dataType) {
      case "leaveType":
        if (option.leaveTypeName === "ANNUAL") return t("common.anual");
        if (option.leaveTypeName === "CASUAL") return t("common.casual");
        if (option.leaveTypeName === "MEDICAL") return t("common.medical");
        return option.leaveTypeName;
      case "userGroup": return option.description;
      case "shift": return option.shiftName;
      case "country": return option.countryName;
      case "administratorRights": return t(`roles.${option.remark}`);
      case "company": return option.companyName;
      case "dropDownShowTime": return option.description;
      case "user": return option.fullName;
      case "group": return option.groupName;
      case "department": return option.departmentName;
      case "device": return option.description;
      case "selecStatusLeave": return option.statusName;
      case "permissionId":
        return (
          option.userRoleName ??
          option.roleName ??
          t(`roles2.${(option.code || option.userRoleName || option.roleName || "").trim()}`) ??
          String(option.userRoleId ?? option.roleId ?? option.id)
        );
      default: return option.label;
    }
  };

  const handleChange = (event) => {
    const next = event.target.value;
    onChange({
      target: {
        name,
        value: multiple
          ? typeof next === "string" ? next.split(",") : next
          : next,
      },
    });
  };

  // giữ value hợp lệ theo options
  const validValue = useMemo(() => {
    return multiple
      ? Array.isArray(value)
        ? value.filter((v) => options?.some((o) => getValue(o) === v))
        : []
      : options?.some((o) => getValue(o) === value) ? value : "";
  }, [multiple, value, options]);

  // fallback mặc định: ưu tiên prop defaultValue; nếu không có thì
  // với administratorRights dùng "EMPLOYEE"
  const computedDefault = useMemo(() => {
    const dv =
      defaultValue ??
      (dataType === "administratorRights" ? "EMPLOYEE" : undefined);
    if (!dv) return multiple ? [] : "";
    return options?.some((o) => getValue(o) === dv) ? dv : (multiple ? [] : "");
  }, [defaultValue, dataType, options, multiple]);

  // giá trị cuối cùng đưa xuống Select
  const effectiveValue = useMemo(() => {
    if (multiple) {
      return (validValue && validValue.length) ? validValue : computedDefault;
    }
    return validValue !== "" ? validValue : computedDefault;
  }, [multiple, validValue, computedDefault]);

  // Optional: đồng bộ ngược về parent khi options load xong mà chưa có value hợp lệ
  useEffect(() => {
    if (!multiple) {
      // nếu parent chưa có value hợp lệ nhưng đã có default hợp lệ -> push lên
      if (validValue === "" && effectiveValue) {
        onChange({ target: { name, value: effectiveValue } });
      }
    } else {
      if (Array.isArray(validValue) && validValue.length === 0 && Array.isArray(effectiveValue) && effectiveValue.length > 0) {
        onChange({ target: { name, value: effectiveValue } });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveValue]); // chạy khi options cập nhật và default đã xác định

  return (
    <StyledSelect
      {...rest}
      value={effectiveValue}             
      onChange={handleChange}            
      size="small"
      disabled={!!readOnly}
      className={`form-control !bg-white ${className} ${readOnly ? '!bg-[#e5e7eb]' : ''}`}
      input={
        <OutlinedInput
          style={{ minHeight: 30, height: 30, borderRadius: 8 }}
          sx={{
            "& .MuiOutlinedInput-input": {
              minHeight: 30,
              height: 30,
              paddingTop: 0,
              paddingBottom: 0,
              display: "flex",
              alignItems: "center",
            },
          }}
        />
      }
      sx={{
        "&&": { minHeight: "30px !important", height: "30px !important", borderRadius: "10px !important" },
        "& .MuiInputBase-root, & .MuiOutlinedInput-root": {
          minHeight: "30px !important",
          height: "30px !important",
          borderRadius: "10px !important",
        },
        "& .MuiSelect-select": {
          minHeight: 30,
          height: 30,
          paddingTop: 0,
          paddingBottom: 0,
          display: "flex",
          alignItems: "center",
          lineHeight: "30px",
          fontSize: "12px",
        },
        ...(sx || {}),
      }}
      MenuProps={{
        anchorOrigin: {
          vertical: position === "top" ? "top" : "bottom",
          horizontal: "left",
        },
        transformOrigin: {
          vertical: position === "top" ? "bottom" : "top",
          horizontal: "left",
        },
        PaperProps: { style: { maxHeight: 200 } },
        disableAutoFocusItem: true,
      }}
      renderValue={(selected) => {
        if (Array.isArray(selected)) {
          return selected
            .map((val) => {
              const found = options.find((o) => getValue(o) === val);
              return found ? getLabel(found) : "";
            })
            .filter(Boolean)
            .join(", ");
        }
        const found = options.find((o) => getValue(o) === selected);
        return found ? getLabel(found) : selected;
      }}
    >
      {options?.map((option, idx) => {
        const val = getValue(option);
        const label = getLabel(option);
        const disabled =
          Array.isArray(disabledUserIds) &&
          (dataType === "user" || dataType === "administratorRights")
            ? disabledUserIds.includes(val)
            : false;

        return (
          <MenuItem
            key={idx}
            value={val}
            disableRipple
            disabled={disabled}
            style={{
              borderBottom:
                (dataType === "shift" || dataType === "device") && val === 0
                  ? "1px solid #ccc"
                  : "none",
            }}
          >
            {multiple && (
              <Checkbox
                checked={Array.isArray(effectiveValue) && effectiveValue.includes(val)}
              />
            )}
            <ListItemText primary={label} />
          </MenuItem>
        );
      })}
    </StyledSelect>
  );
};

export default CustomSelectNew;
